import { useState } from 'react';

import styled, { keyframes, css } from 'styled-components';

import { getDisplayLabel, getFieldNames, normalizeAvailableFields } from '../Utils/aiFieldConfig';

import UserProfile from '../../Component/Normal/UserProfile';
import { BulkSmsSection, InlineSmsCell, InlineSmsSendCell } from './SmsRecommendPanel';

import DefaultProfile from '../../image/DefaultProfile.png';

// ============================================
// DisplayRenderer — 테이블 기반 렌더링
// ============================================

export default function DisplayRenderer({
  displayItems, theme, isDark, isNewResponse, isSameDisplay,
  smsRecommendation, onSmsSend, onIndividualSmsSend, onIndividualSmsGenerate,
}) {
  const { 
    items, columns, columnLabels, columnFormats,
    numericColumns, intro, outro,
  } = displayItems;
  const fieldsMeta = normalizeAvailableFields(displayItems.availableFields);

  const PROFILE_FIELDS = getFieldNames('profile');
  const hasProfile = items.some(item => PROFILE_FIELDS.some(f => item[f] != null));
  const smsMode = smsRecommendation?.mode;

  const renderContent = () => (
    <DataTable
      items={items}
      columns={columns}
      columnLabels={columnLabels}
      columnFormats={columnFormats}
      fieldsMeta={fieldsMeta}
      theme={theme}
      isDark={isDark}
      numericColumns={numericColumns}
      hasProfile={hasProfile}
      isNewResponse={isNewResponse}
      smsRecommendation={smsMode === 'individual' ? smsRecommendation : null}
      onIndividualSmsGenerate={onIndividualSmsGenerate}
      onIndividualSmsSend={onIndividualSmsSend}
    />
  );

  return (
    <DisplayWrapper>
      {
        intro &&
        <IntroText color={theme.text}>{intro}</IntroText>
      }
      <ContentBox $bg={isDark ? theme.subBackground : theme.background}>
        {isSameDisplay && smsMode === 'bulk' ? null : (
          isNewResponse ? (
            <ContentAppear>{renderContent()}</ContentAppear>
          ) : (
            renderContent()
          )
        )}
        {
          smsMode === 'bulk' && smsRecommendation &&
          <BulkSmsSection
            recommendation={smsRecommendation}
            onSend={onSmsSend}
            theme={theme}
          />
        }
      </ContentBox>
      {
        outro &&
        <OutroText color={theme.text}>{outro}</OutroText>
      }
    </DisplayWrapper>
  );
}

// ============================================
// dot path 값 조회 (flat key 우선 → 경로 순회 → 마지막 segment fallback)
// ============================================

function getByDotPath(obj, path) {
  if (obj == null) return undefined;
  if (path in obj) return obj[path];
  if (!path.includes('.')) return undefined;
  const traversed = path.split('.').reduce((o, k) => o?.[k], obj);

  if (traversed !== undefined) return traversed;
  return obj[path.split('.').pop()];
}

// dot path의 마지막 segment 추출
function lastSegment(path) {
  return path.includes('.') ? path.split('.').pop() : path;
}

// ============================================
// DataTable
// ============================================

function DataTable({
  items, columns, columnLabels, columnFormats, fieldsMeta, theme, isDark, numericColumns, hasProfile, isNewResponse,
  smsRecommendation, onIndividualSmsGenerate, onIndividualSmsSend,
}) {
  const nameCol = columns.find(col => MEMBER_NAME_FIELDS.includes(lastSegment(col)));
  const phoneCol = columns.find(col => PHONE_FIELDS.includes(lastSegment(col))) || PHONE_FIELDS.find(f => items.some(item => item[f] != null));
  const hasMemberCell = hasProfile && nameCol;

  const uniqueColumns = [...new Set(columns)];
  const regularColumns = hasMemberCell
    ? uniqueColumns.filter(col => col !== nameCol && (phoneCol ? col !== phoneCol : true))
    : uniqueColumns;

  const hasIndividualSms = smsRecommendation?.mode === 'individual';
  const headerColor = isDark ? theme.text : theme.white;

  // 그룹핑 키 결정: memberId → teacherId 순으로 탐색
  const findGroupKey = () => {
    if (items.some(i => i.memberId != null)) return 'memberId';
    if (items.some(i => i.teacherId != null)) return 'teacherId';
    return null;
  };
  const groupKey = findGroupKey();
  const firstOfGroupFlags = (() => {
    const seen = new Set();
    return items.map(item => {
      if (!groupKey || !item[groupKey]) return true;
      if (seen.has(item[groupKey])) return false;
      seen.add(item[groupKey]);
      return true;
    });
  })();

  return (
    <TableContainer>
      <TableWrapper $thumb={theme.scrollBar}>
        <StyledTable>
          <thead>
            <HeaderRow $bg={theme.primary}>
              {
                hasMemberCell &&
                <HeaderCell $isFirst={true} $color={headerColor}>회원</HeaderCell>
              }
              {regularColumns.map((col, ci) => (
                <HeaderCell key={col} $isFirst={!hasMemberCell && ci === 0} $color={headerColor}>
                  {columnLabels[col] || col}
                </HeaderCell>
              ))}
              {
                hasIndividualSms &&
                <HeaderCell $color={headerColor}>추천 문자</HeaderCell>
              }
              {
                hasIndividualSms &&
                <HeaderCell $color={headerColor}>문자 발송</HeaderCell>
              }
            </HeaderRow>
          </thead>
          <tbody>
            {items.map((item, idx) => {
              const isFirstOfGroup = firstOfGroupFlags[idx];

              return (
                <BodyRow key={'BodyRow-' + idx} $animate={isNewResponse} style={isNewResponse ? { '--item-index': idx } : undefined}>
                  {hasMemberCell && (
                    <BodyCell $isMember={true}>
                      {isFirstOfGroup ? (
                        <MemberCell item={item} nameCol={nameCol} phoneCol={phoneCol} theme={theme} />
                      ) : null}
                    </BodyCell>
                  )}
                  {regularColumns.map(col => (
                    <BodyCell
                      key={col}
                      color={theme.text}
                      $isNumeric={numericColumns?.includes(col)}
                    >
                      {isPhoneColumn(col) ? (
                        <PhoneCopyCell value={getByDotPath(item, col)} theme={theme} />
                      ) : (
                        formatCellValue(getByDotPath(item, col), lastSegment(col), columnFormats?.[col], fieldsMeta?.[col] ?? fieldsMeta?.[lastSegment(col)])
                      )}
                    </BodyCell>
                  ))}
                  {
                    hasIndividualSms &&
                    <BodyCell $isSms={true}>
                      <InlineSmsCell
                        memberId={item.memberId}
                        individualTexts={smsRecommendation.individualTexts}
                        onGenerate={() => onIndividualSmsGenerate(item.memberId, smsRecommendation.recipients?.find(r => r.memberId === item.memberId), smsRecommendation.context)}
                        theme={theme}
                      />
                    </BodyCell>
                  }
                  {
                    hasIndividualSms &&
                    <BodyCell>
                      <InlineSmsSendCell
                        memberId={item.memberId}
                        individualTexts={smsRecommendation.individualTexts}
                        onGenerate={() => onIndividualSmsGenerate(item.memberId, smsRecommendation.recipients?.find(r => r.memberId === item.memberId), smsRecommendation.context)}
                        onSend={onIndividualSmsSend}
                        theme={theme}
                      />
                    </BodyCell>
                  }
                </BodyRow>
              );
            })}
          </tbody>
        </StyledTable>
      </TableWrapper>
    </TableContainer>
  );
}

// ============================================
// MemberCell (회원 복합 셀: 아바타 + 이름 + 연락처)
// ============================================

const MEMBER_NAME_FIELDS = getFieldNames('name');
const MEMBER_PROFILE_FIELDS = getFieldNames('profile');

function MemberCell({ item, nameCol, phoneCol, theme }) {
  const name = getByDotPath(item, nameCol) || '-';
  const phone = phoneCol ? getByDotPath(item, phoneCol) : null;
  const profileValue = MEMBER_PROFILE_FIELDS.reduce((v, f) => v || item[f], null);

  return (
    <MemberCellWrapper>
      <MemberProfileWrapper>
        <UserProfile profile={profileValue} defaultProfile={DefaultProfile} />
      </MemberProfileWrapper>
      <MemberInfo>
        <MemberName color={theme.text}>{name}</MemberName>
        {phone && <PhoneCopyCell value={phone} theme={theme} isSub={true} />}
      </MemberInfo>
    </MemberCellWrapper>
  );
}

// ============================================
// PhoneCopyCell (전화번호 복사 버튼)
// ============================================

const PHONE_FIELDS = getFieldNames('phone');

function isPhoneColumn(col) {
  const seg = lastSegment(col).toLowerCase();
  return PHONE_FIELDS.some(f => seg === f.toLowerCase());
}

function PhoneCopyCell({ value, theme, isSub }) {
  const [isCopied, setIsCopied] = useState(false);

  function onClickCopy() {
    if (!value) return;
    navigator.clipboard.writeText(String(value));
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 1500);
  }

  return (
    <PhoneText onClick={onClickCopy} $hasValue={!!value} $color={isSub ? theme.subText : theme.text} $isSub={isSub}>
      {formatCellValue(value)}
      {!!value && (
        <CopyIcon $copied={isCopied} $color={theme.primary}>
          {isCopied ? (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          )}
        </CopyIcon>
      )}
    </PhoneText>
  );
}

// ============================================
// 유틸
// ============================================

function formatCellValue(val, col, formatType, fieldMeta) {
  if (val == null || val === '') return '정보 없음';

  // 딕셔너리 기반 값 → 라벨 변환 (백엔드 메타 우선 → 정적 DISPLAY_LABELS fallback)
  if (col) {
    const label = getDisplayLabel(col, val, fieldMeta);

    if (label) return label;
  }

  if (typeof val === 'boolean') return val ? '🟢' : '❌';

  // 포맷 타입 기반 변환 (딕셔너리 compute 연동)
  if (formatType === 'birthday' && typeof val === 'string' && /^\d{8}$/.test(val)) {
    return `${val.slice(0, 4)}.${val.slice(4, 6)}.${val.slice(6, 8)}`;
  }
  if (formatType === 'dday' && typeof val === 'number') {
    return val > 0 ? `D-${val}` : val === 0 ? 'D-Day' : `D+${Math.abs(val)}`;
  }

  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val)) return val.split('T')[0];
  if (typeof val === 'number') return val.toLocaleString();
  if (Array.isArray(val)) {
    return val.map(v => typeof v === 'object' ? summarizeObject(v) : String(v)).join(', ');
  }
  if (typeof val === 'object') return summarizeObject(val);
  return String(val);
}

// 이름 필드 우선순위 //
const NAME_KEYS = ['name', 'membershipName', 'viewMembershipName', 'lessonName', 'productName', 'title', 'label'];

function summarizeObject(obj) {
  if (obj == null) return '-';

  // 이름 필드가 있으면 이름만 반환 (날짜는 별도 컬럼으로 표시)
  const nameVal = NAME_KEYS.reduce((found, key) => found || obj[key], null);

  if (nameVal) return String(nameVal);

  const vals = Object.entries(obj)
    .filter(([, v]) => v != null && typeof v !== 'object' && !/^\d{4,}$/.test(String(v)))
    .slice(0, 3)
    .map(([, v]) => {
      if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.split('T')[0];
      return String(v);
    });
  return vals.join(' / ') || '-';
}

const expandIn = keyframes`
  from { clip-path: inset(0 0 100% 0); opacity: 0; }
  to { clip-path: inset(0 0 0 0); opacity: 1; }
`;

const rowFadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const DisplayWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: fit-content;
  max-width: 100%;

  gap: 10px;
`;

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;

  align-self: flex-start;

  background-color: ${props => props.$bg};

  border-radius: 5px;

  overflow: hidden;
`;

const ContentAppear = styled.div`
  animation: ${expandIn} 0.6s ease-out forwards;
`;

const IntroText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;
  line-height: 24px;

  color: ${props => props.color};

  margin: 0;
`;

const OutroText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;
  line-height: 24px;

  color: ${props => props.color};

  margin: 4px 0 0 0;
`;

// DataTable Styles //

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;

  gap: 6px;
`;


const TableWrapper = styled.div`
  overflow-x: auto;
  overflow-y: hidden;

  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.$thumb};
    border-radius: 4px;
  }

  scrollbar-width: thin;
  scrollbar-color: ${props => props.$thumb} transparent;
`;

const StyledTable = styled.table`
  border-collapse: collapse;

  table-layout: auto;
`;

const HeaderRow = styled.tr`
  background-color: ${props => props.$bg};
`;


const HeaderCell = styled.th`
  font-family: NotoSansKR-Medium;
  font-size: 14px;
  line-height: 24px;

  color: ${props => props.$color};

  padding: 8px 12px;

  text-align: left;
  white-space: nowrap;

  ${props => props.$isFirst && 'width: 36px; text-align: center;'}
`;

const BodyRow = styled.tr`
  ${props => props.$animate && css`
    opacity: 0;
    animation: ${rowFadeIn} 0.3s ease-out forwards;
    animation-delay: calc(var(--item-index, 0) * 0.06s + 0.15s);
  `}
`;

const BodyCell = styled.td`
  font-family: NotoSansKR-Regular;
  font-size: 14px;
  line-height: 24px;

  color: ${props => props.color};

  padding: 7px 12px;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${props => props.$isNumeric && 'text-align: right;'}
  ${props => props.$isMember && 'overflow: visible; white-space: normal; max-width: none;'}
`;

// MemberCell Styles //

const MemberCellWrapper = styled.div`
  display: flex;
  align-items: center;

  gap: 8px;
`;

const MemberProfileWrapper = styled.div`
  width: 40px;
  height: 40px;

  border-radius: 5px;

  overflow: hidden;

  flex-shrink: 0;

  & img {
    object-fit: cover;
  }
`;

const MemberInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const MemberName = styled.span`
  font-family: NotoSansKR-Regular;
  font-size: 14px;
  line-height: 22px;

  color: ${props => props.color};
`;

// PhoneCopyCell Styles //

const CopyIcon = styled.span`
  display: inline-flex;
  align-items: center;

  color: ${props => props.$copied ? props.$color : 'currentColor'};

  opacity: ${props => props.$copied ? 1 : 0};

  transition: opacity 0.15s ease, color 0.15s ease;
`;

const PhoneText = styled.span`
  display: inline-flex;
  align-items: center;

  gap: 4px;

  font-size: 14px;
  line-height: ${props => props.$isSub ? '18px' : 'inherit'};

  color: ${props => props.$color};

  cursor: ${props => props.$hasValue ? 'pointer' : 'default'};

  transition: color 0.2s ease;

  &:hover ${CopyIcon} {
    opacity: 1;
  }
`;

